import { DragEndEvent, DragStartEvent, useDndMonitor } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { equals, update } from 'ramda';
import React, { useState, ReactNode, useCallback, useEffect, useMemo } from 'react';

import { isDefined } from 'common/utils';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';
import { OfferBinding, SelectionCategory } from 'modules/shared-menu/types';

import { useDndMethods } from './useDndMethods';
import { SharedMenuElement } from '../../../types';
import { isSharedMenuOffer } from '../../../utils';
import { MenuEditorDragEventData } from '../types';
import { getMenuEditorDragEventData } from '../utils';

type Props = {
  category: SelectionCategory | null;
  menuEditorList: SharedMenuElement[];
  onClearSelection?: () => void;
  children: (data: {
    draggingId?: string;
    draggingType?: MenuEditorDragEventData['type'];
    displayMenuEditorList: SharedMenuElement[];
  }) => ReactNode;
};

const CollectionDndMonitor = ({ menuEditorList, children, category, onClearSelection = () => undefined }: Props) => {
  const { menuEditorPage } = useListingManagerRoutes();
  // Use cached active due to the issue with virtualized lists cause the active.current to get lost: https://github.com/clauderic/dnd-kit/issues/794
  const [activeEvent, setActiveEvent] = useState<null | MenuEditorDragEventData>(null);
  const [localMenuEditorList, setLocalMenuEditorList] = useState<null | undefined | SharedMenuElement[]>(
    menuEditorList,
  );
  const allOfferBindings = useMemo(() => category?.offer_bindings ?? [], [category?.offer_bindings]);

  const { onUpdateOfferBindingOrder, onMoveOffersToCategory } = useDndMethods({
    onClearSelection,
    categoryId: category?.id ?? null,
    menuEditorRootUrl: menuEditorPage(),
  });

  const reset = useCallback(() => {
    setLocalMenuEditorList(null);
    setActiveEvent(null);
  }, []);

  useEffect(() => {
    setActiveEvent(null);
    setLocalMenuEditorList(menuEditorList);
  }, [menuEditorList]);

  const buildUpdatedOfferBindings = useCallback(
    (selectedItemIds: Set<string>, activeItemId: string, overItemId: string) => {
      const selectedOfferBindings = allOfferBindings.filter(item => selectedItemIds.has(item.offer_id));
      const visibleOfferBindings = allOfferBindings.filter(
        categoryItem => !selectedItemIds.has(categoryItem.offer_id) || categoryItem.offer_id === activeItemId,
      );

      const newIndex = visibleOfferBindings.findIndex(categoryItem => categoryItem.offer_id === overItemId);
      const oldIndex = visibleOfferBindings.findIndex(categoryItem => categoryItem.offer_id === activeItemId);

      const updatedOfferBindings = update<OfferBinding | OfferBinding[]>(
        newIndex,
        selectedOfferBindings,
        arrayMove(visibleOfferBindings, oldIndex, newIndex),
      ).flat();
      return updatedOfferBindings;
    },
    [allOfferBindings],
  );

  const buildLocalReorderedItemsMenuList = useCallback(
    (offerBindings: OfferBinding[]) =>
      offerBindings
        .map(offerBinding =>
          menuEditorList
            .filter(isSharedMenuOffer)
            .find(menuEditorItem => menuEditorItem.itemState.id === offerBinding.offer_id),
        )
        .filter(isDefined),
    [menuEditorList],
  );

  const onDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const activeEvent = active && getMenuEditorDragEventData(active);
      if (!activeEvent) {
        return;
      }

      setActiveEvent(activeEvent);

      if (activeEvent.type === 'DND_EDITOR_MULTIPLE_ITEMS' && onUpdateOfferBindingOrder) {
        const hiddenItemIds = Array.from(activeEvent.selectedIds?.values() ?? []).filter(id => id !== active.id);
        const updatedDisplayMenuList = menuEditorList.filter(
          menuEditorItem =>
            isSharedMenuOffer(menuEditorItem) && !hiddenItemIds.some(id => id === menuEditorItem.itemState.id),
        );

        return setLocalMenuEditorList(updatedDisplayMenuList);
      }
    },
    [menuEditorList, onUpdateOfferBindingOrder],
  );

  const onDragEnd = useCallback(
    async ({ over }: DragEndEvent) => {
      try {
        const overEvent = over && getMenuEditorDragEventData(over);
        if (!activeEvent || !overEvent) {
          return;
        }

        // Moving single offer to a category/subcategory/no-category
        // Target & source categories can not be the same
        if (
          ['DND_EDITOR_SINGLE_ITEM', 'DND_EDITOR_SINGLE_NO_CATEGORY_ITEM'].includes(activeEvent.type) &&
          ['DND_NAVIGATION_CATEGORY', 'DND_NAVIGATION_SUBCATEGORY', 'DND_NAVIGATION_NO_CATEGORY'].includes(
            overEvent.type,
          ) &&
          category?.id !== overEvent.id
        ) {
          setLocalMenuEditorList(
            menuEditorList.filter(item => item.type !== 'item' || item.itemState.id !== activeEvent.id),
          );
          const targetCategoryId = overEvent.type === 'DND_NAVIGATION_NO_CATEGORY' ? null : overEvent.id;
          return await onMoveOffersToCategory(activeEvent.id, targetCategoryId);
        }

        // Moving multiple offers to a category/subcategory
        // Target & source categories can not be the same
        if (
          (activeEvent.type === 'DND_EDITOR_MULTIPLE_ITEMS' ||
            activeEvent.type === 'DND_EDITOR_MULTIPLE_NO_CATEGORY_ITEMS') &&
          ['DND_NAVIGATION_CATEGORY', 'DND_NAVIGATION_SUBCATEGORY', 'DND_NAVIGATION_NO_CATEGORY'].includes(
            overEvent.type,
          ) &&
          category?.id !== overEvent.id
        ) {
          const targetCategoryId = overEvent.type === 'DND_NAVIGATION_NO_CATEGORY' ? null : overEvent.id;
          const { selectedIds } = activeEvent;
          setLocalMenuEditorList(oldState =>
            oldState?.filter(listItem => listItem.type !== 'item' || !selectedIds.has(listItem.itemState.id)),
          );
          return await onMoveOffersToCategory(activeEvent.id, targetCategoryId, activeEvent.selectedIds);
        }

        // Changing the position of a single offer in a category
        if (
          onUpdateOfferBindingOrder &&
          activeEvent.type === 'DND_EDITOR_SINGLE_ITEM' &&
          (overEvent.type === 'DND_EDITOR_SINGLE_ITEM' || overEvent.type === 'DND_EDITOR_MULTIPLE_ITEMS')
        ) {
          const localOfferBindings = buildUpdatedOfferBindings(new Set([activeEvent.id]), activeEvent.id, overEvent.id);
          setLocalMenuEditorList(buildLocalReorderedItemsMenuList(localOfferBindings));
          if (equals(localOfferBindings, allOfferBindings)) {
            return reset();
          }
          const reorderedItemBindingIds = localOfferBindings.map(offerBinding => offerBinding.offer_id);
          return await onUpdateOfferBindingOrder(reorderedItemBindingIds);
        }

        // Changing the position of multiple offers in a category
        if (
          onUpdateOfferBindingOrder &&
          activeEvent.type === 'DND_EDITOR_MULTIPLE_ITEMS' &&
          (overEvent.type === 'DND_EDITOR_SINGLE_ITEM' || overEvent.type === 'DND_EDITOR_MULTIPLE_ITEMS')
        ) {
          const localOfferBindings = buildUpdatedOfferBindings(activeEvent.selectedIds, activeEvent.id, overEvent.id);
          setLocalMenuEditorList(buildLocalReorderedItemsMenuList(localOfferBindings));
          if (equals(localOfferBindings, allOfferBindings)) {
            return reset();
          }
          const reorderedItemBindingIds = localOfferBindings.map(itemBinding => itemBinding.offer_id);
          return await onUpdateOfferBindingOrder(reorderedItemBindingIds);
        }
      } finally {
        reset();
      }
    },
    [
      activeEvent,
      category?.id,
      allOfferBindings,
      buildUpdatedOfferBindings,
      buildLocalReorderedItemsMenuList,
      reset,
      menuEditorList,
      onUpdateOfferBindingOrder,
      onMoveOffersToCategory,
    ],
  );

  useDndMonitor({
    onDragStart,
    onDragEnd,
  });

  return (
    <>
      {children({
        draggingId: activeEvent?.id,
        draggingType: activeEvent?.type,
        displayMenuEditorList: localMenuEditorList ?? menuEditorList ?? [],
      })}
    </>
  );
};

export default React.memo(CollectionDndMonitor);
